import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translateErrorMessage } from '../../utils/common';
import { isEVMChain } from '../../wallet/chains';
import { WEB3_ADDRESS } from '../../wallet';
import { ConnectionState } from '../../wallet/types';
export function useWalletAuth({ walletConnection, walletSession, toast, handleDisconnect, evmWalletSigner, signMessage, publicKey, curEvmChainId, }) {
    const challengingStatus = useRef(false);
    const [isSigningChallenge, setIsSigningChallenge] = useState(false);
    const getWalletAuthChain = useCallback((network) => {
        return isEVMChain(network) ? 'EVM' : 'SVM';
    }, []);
    const uint8ToBase64 = useCallback((bytes) => {
        let binary = '';
        bytes.forEach((byte) => {
            binary += String.fromCharCode(byte);
        });
        return window.btoa(binary);
    }, []);
    const isWalletAuthenticated = useMemo(() => {
        if (walletConnection.connectionState !== ConnectionState.Connected ||
            !walletConnection.address) {
            return false;
        }
        if (!walletSession)
            return true;
        return (walletSession.loginStatus === 'success' &&
            !!walletSession.user?.walletAddress &&
            walletSession.user.walletAddress.toLowerCase() === walletConnection.address.toLowerCase());
    }, [walletSession, walletConnection]);
    const isWalletSigningIn = useMemo(() => {
        if (!isSigningChallenge)
            return false;
        if (isWalletAuthenticated)
            return false;
        if (walletConnection.connectionState !== ConnectionState.Connected ||
            !walletConnection.address) {
            return false;
        }
        return Boolean(walletSession);
    }, [walletSession, walletConnection, isWalletAuthenticated, isSigningChallenge]);
    const connectWalletWithSign = useCallback(async () => {
        if (!walletSession)
            return;
        const { user, accessTokenVerified } = walletSession;
        if (!walletConnection?.network ||
            !walletConnection?.address ||
            walletConnection.connectionState != ConnectionState.Connected)
            return;
        if (!accessTokenVerified ||
            (user?.walletAddress && user?.walletAddress == walletConnection.address))
            return;
        const chain = getWalletAuthChain(walletConnection.network);
        if (chain === 'EVM' &&
            (!evmWalletSigner ||
                evmWalletSigner.address.toLowerCase() != walletConnection.address.toLowerCase())) {
            return;
        }
        if (chain === 'SVM' && (!signMessage || publicKey?.toString() != walletConnection.address)) {
            return;
        }
        if (challengingStatus.current)
            return;
        challengingStatus.current = true;
        setIsSigningChallenge(true);
        try {
            const challengePayload = chain === 'EVM'
                ? {
                    walletAddress: walletConnection.address,
                    chain,
                    uri: window.location.origin,
                    chainId: curEvmChainId ?? WEB3_ADDRESS[walletConnection.network]?.chainId,
                }
                : {
                    walletAddress: walletConnection.address,
                    chain,
                };
            const challengeResponse = await walletSession.getWalletChallenge(challengePayload);
            const challengeData = challengeResponse?.data?.data;
            if (!challengeData?.message || !challengeData?.nonce) {
                throw new Error('Unable to create wallet challenge.');
            }
            let signature = '';
            if (chain === 'EVM') {
                signature = await evmWalletSigner.signMessage(challengeData.message);
            }
            else {
                const signatureBytes = await signMessage(new TextEncoder().encode(challengeData.message));
                signature = uint8ToBase64(signatureBytes);
            }
            await walletSession.connectToWallet({
                walletAddress: walletConnection.address,
                role: 'USER',
                type: 'USER',
                chain,
                nonce: challengeData.nonce,
                message: challengeData.message,
                signature,
            });
        }
        catch (error) {
            const err = error;
            const msg = translateErrorMessage(error);
            if (!err.message.includes('rejected') && !`${error}`.includes('4100')) {
                toast.error(`Wallet authentication failed: ${msg}`);
            }
            handleDisconnect();
        }
        finally {
            challengingStatus.current = false;
            setIsSigningChallenge(false);
        }
    }, [
        walletSession,
        walletConnection,
        getWalletAuthChain,
        evmWalletSigner,
        signMessage,
        uint8ToBase64,
        publicKey,
        curEvmChainId,
        toast,
        handleDisconnect,
    ]);
    const handleDisconnectRef = useRef(handleDisconnect);
    handleDisconnectRef.current = handleDisconnect;
    useEffect(() => {
        walletSession?.verifyAccessToken?.();
        // Verify session once on mount (same as cai-frontend).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Disconnect once when auth fails. Do not depend on handleDisconnect identity —
    // an unstable callback + sticky loginStatus === 'error' causes an infinite Redux loop.
    useEffect(() => {
        if (walletSession?.loginStatus !== 'error')
            return;
        handleDisconnectRef.current();
    }, [walletSession?.loginStatus]);
    useEffect(() => {
        if (!walletSession?.accessTokenVerified)
            return;
        if (walletConnection.connectionState !== ConnectionState.Connected)
            return;
        if (!walletConnection.address)
            return;
        if (walletSession.user?.walletAddress?.toLowerCase() === walletConnection.address.toLowerCase())
            return;
        connectWalletWithSign();
    }, [
        walletSession?.accessTokenVerified,
        walletSession?.user?.walletAddress,
        walletConnection.connectionState,
        walletConnection.address,
        connectWalletWithSign,
    ]);
    return {
        isWalletAuthenticated,
        isWalletSigningIn,
        connectWalletWithSign,
    };
}
